// Стандартные директивы AngularJS 1.2, которые используют шаблоны сайта и блоки доставок.
// Поведение повторяет 1.2.29: ng-show, ng-hide и ng-if считают ложными также '0', 'false', 'no' и [].
import { equals } from './filters';
import { copy } from './scope';
import { reportError } from './report';

export function toBoolean(value) {
    if (typeof value === 'function') {
        return true;
    }
    if (value && value.length !== 0) {
        const v = ('' + value).toLowerCase();
        return !(v === 'f' || v === '0' || v === 'false' || v === 'no' || v === 'n' || v === '[]');
    }
    return false;
}

// Классы из ng-class: объект {класс: условие} или строка через пробел.
function classList(value) {
    if (typeof value === 'string') {
        return value.split(' ');
    }
    if (value && typeof value === 'object') {
        return Object.keys(value).filter(key => value[key]).join(' ').split(' ');
    }
    return [];
}

export function registerCoreDirectives(directives) {
    directives.set('ngController', {
        priority: 500,
        scope: true,
        controller(scope, element, attrs, runtime) {
            const controller = runtime.controllers.get(attrs.ngController);
            if (!controller) {
                reportError(`Контроллер «${attrs.ngController}» не найден.`);
                return undefined;
            }
            return controller(scope, element, attrs) || {};
        },
    });

    directives.set('ngInit', {
        priority: 450,
        pre(scope, element, attrs) {
            scope.$eval(attrs.ngInit);
        },
    });

    directives.set('ngBind', {
        link(scope, element, attrs) {
            scope.$watch(attrs.ngBind, value => {
                element.textContent = value == undefined ? '' : value; // eslint-disable-line eqeqeq
            });
        },
    });

    directives.set('ngBindHtml', {
        link(scope, element, attrs, runtime) {
            const getter = runtime.parse(attrs.ngBindHtml);
            scope.$watch(s => (getter(s) || '').toString(), () => {
                element.innerHTML = getter(scope) || '';
            });
        },
    });

    directives.set('ngShow', {
        link(scope, element, attrs, runtime) {
            scope.$watch(attrs.ngShow, value => {
                runtime.animate[toBoolean(value) ? 'removeClass' : 'addClass'](element, 'ng-hide');
            });
        },
    });

    directives.set('ngHide', {
        link(scope, element, attrs, runtime) {
            scope.$watch(attrs.ngHide, value => {
                runtime.animate[toBoolean(value) ? 'addClass' : 'removeClass'](element, 'ng-hide');
            });
        },
    });

    directives.set('ngIf', {
        priority: 600,
        terminal: true,
        transclude: 'element',
        link(scope, anchor, attrs, runtime, transclude) {
            let block = null;
            let childScope = null;
            let hydrate = transclude.hydrate;
            // После первого решения transclude хранит только чистый шаблон.
            delete transclude.hydrate;
            scope.$watch(attrs.ngIf, value => {
                if (toBoolean(value)) {
                    if (!childScope) {
                        childScope = scope.$new();
                        const end = document.createComment(` end ngIf: ${attrs.ngIf} `);
                        const initial = hydrate;
                        hydrate = null;
                        const clone = (initial || transclude)(childScope, node => {
                            if (initial) {
                                anchor.parentNode.insertBefore(end, node.nextSibling);
                            } else {
                                anchor.parentNode.insertBefore(end, anchor.nextSibling);
                                anchor.parentNode.insertBefore(node, end);
                            }
                        });
                        block = { clone, end };
                        if (!initial && clone.isConnected) {
                            runtime.animate.enter(clone, anchor.parentNode, anchor);
                        }
                    }
                } else {
                    if (hydrate) {
                        hydrate();
                        hydrate = null;
                    }
                    if (childScope) {
                        childScope.$destroy();
                        childScope = null;
                    }
                    if (block) {
                        // Принятый DOM ссылается на собственный anchor.
                        // Он остаётся в DOM для следующих включений ng-if.
                        const replaced = block.clone.__sdReplacedBy;
                        const start = replaced && replaced !== anchor ? replaced : block.clone;
                        const nodes = [];
                        for (let node = start; node; node = node.nextSibling) {
                            nodes.push(node);
                            if (node === block.end) {
                                break;
                            }
                        }
                        nodes.forEach(node => {
                            if (node.nodeType === 1) {
                                runtime.animate.leave(node);
                            } else if (node.parentNode) {
                                node.parentNode.removeChild(node);
                            }
                        });
                        block = null;
                    }
                }
            });
        },
    });

    directives.set('ngClass', {
        link(scope, element, attrs, runtime) {
            let oldValue;
            scope.$watch(attrs.ngClass, value => {
                const added = classList(value);
                if (!oldValue) {
                    added.forEach(name => name && runtime.animate.addClass(element, name));
                } else if (!equals(value, oldValue)) {
                    const removed = classList(oldValue);
                    removed.filter(name => name && added.indexOf(name) === -1).forEach(name => runtime.animate.removeClass(element, name));
                    added.filter(name => name && removed.indexOf(name) === -1).forEach(name => runtime.animate.addClass(element, name));
                }
                oldValue = copy(value);
            }, true);
        },
    });

    ['src', 'href'].forEach(attrName => {
        const normalized = 'ng' + attrName.charAt(0).toUpperCase() + attrName.slice(1);
        directives.set(normalized, {
            priority: 99,
            link(scope, element, attrs) {
                attrs.$observe(normalized, value => {
                    if (!value) {
                        if (attrName === 'href') {
                            attrs.$set(attrName, null, true, attrName);
                        }
                        return;
                    }
                    attrs.$set(attrName, value, true, attrName);
                });
            },
        });
    });

    ['disabled', 'checked'].forEach(attrName => {
        const normalized = 'ng' + attrName.charAt(0).toUpperCase() + attrName.slice(1);
        directives.set(normalized, {
            priority: 100,
            link(scope, element, attrs) {
                scope.$watch(attrs[normalized], value => {
                    attrs.$set(attrName, !!value, true, attrName);
                });
            },
        });
    });

    directives.set('ngClick', {
        link(scope, element, attrs, runtime) {
            const fn = runtime.parse(attrs.ngClick);
            element.addEventListener('click', event => {
                scope.$apply(() => fn(scope, { $event: event }));
            });
        },
    });

    // Ссылка без href не должна никуда переходить (как htmlAnchorDirective в AngularJS).
    directives.set('a', {
        restrict: 'E',
        link(scope, element, attrs) {
            if (!attrs.href && !attrs.xlinkHref && !attrs.name) {
                element.addEventListener('click', event => {
                    if (!element.getAttribute('href')) {
                        event.preventDefault();
                    }
                });
            }
        },
    });
}
